import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { persistDizeeUser, postDataAPI } from '../lib/api'
import { trackLogin, setGA4UserId, setGA4UserProperties } from '../lib/analytics'
import OnboardingShell from './signup/steps/OnboardingShell'
import TextField from '../components/ui/TextField'

export default function LoginOtpPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const query = useMemo(() => new URLSearchParams(location.search), [location.search])

  const email = query.get('email') || ''
  const from = query.get('from') || '/'

  const [code, setCode] = useState('')
  const [resendTimer, setResendTimer] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!email) navigate('/login', { replace: true })
  }, [email, navigate])

  useEffect(() => {
    if (!email) return
    postDataAPI('auth/sendOtpToEmail', { email }).catch(() => {})
    setResendTimer(30)
  }, [email])

  useEffect(() => {
    if (resendTimer <= 0) return
    const t = window.setTimeout(() => setResendTimer((s) => s - 1), 1000)
    return () => window.clearTimeout(t)
  }, [resendTimer])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    if (code.trim().length !== 6) return
    setError(null)
    setIsSubmitting(true)
    try {
      const res = await postDataAPI('auth/loginWithOtp', { email, code: code.trim(), phone: '' })
      if (res?.status !== 200) throw new Error(res?.message || 'Login failed')

      const user = res?.data?.user
      const token = res?.data?.token
      if (user) persistDizeeUser(user as Record<string, unknown>)
      if (token) localStorage.setItem('dizeeToken', JSON.stringify(token))
      trackLogin('otp')
      if (user?._id) setGA4UserId(user._id)
      if (user?.professionRole) {
        setGA4UserProperties({
          user_role: user.professionRole,
          user_plan: user.plan?.toString() || 'basic',
          has_team: user.selectedArtists?.length > 0 ? 'true' : 'false',
        })
      }
      localStorage.removeItem('isNewSignup')
      localStorage.removeItem('selectedArtist')
      localStorage.removeItem('dizee:activeArtistIds')
      localStorage.removeItem('dizee:artistCache')

      navigate(from || '/', { replace: true })
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Login failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  const resend = async () => {
    if (!email) return
    if (resendTimer > 0) return
    setError(null)
    try {
      await postDataAPI('auth/sendOtpToEmail', { email })
      setResendTimer(30)
      setCode('')
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to resend code')
    }
  }

  return (
    <OnboardingShell title="Code has been sent to your email" footer="Check your email inbox and spam folder for the verification code.">
      <form onSubmit={submit} className="space-y-5">
        <TextField
          value={code}
          onChange={(v) => setCode(v.replace(/[^0-9]/g, '').slice(0, 6))}
          placeholder="6-digit code"
          inputMode="numeric"
          error={error}
        />

        <button type="submit" disabled={code.trim().length !== 6 || isSubmitting} className="ds-btn ds-btn-primary w-full">
          {isSubmitting ? 'Signing in…' : 'Confirm'}
        </button>

        <button
          type="button"
          onClick={resend}
          disabled={resendTimer > 0}
          className="text-sm text-gray-500 hover:text-gray-900 disabled:opacity-50"
        >
          {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
        </button>
      </form>
    </OnboardingShell>
  )
}

